import type { SipTrafficEvent } from '../services/api';
import { csvDocument } from './csv';

export const SIP_TRAFFIC_PAGE_SIZE = 50;
export const SIP_TRACE_PAGE_SIZE = 200;

export function uniqueCallIds(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

/** Identity of the URL-owned trace scope, excluding pagination and time bounds. */
export function traceScopeKey(callId?: string, relatedCallIds?: string): string {
  return `${callId ?? ''}|${relatedCallIds ?? ''}`;
}

export function relatedCallIdsParam(values: readonly string[]): string | undefined {
  const ids = uniqueCallIds(values);
  if (ids.length > 100) {
    throw new RangeError('A maximum of 100 Call-ID values is supported');
  }
  if (ids.some((id) => id.length > 1024)) {
    throw new RangeError('Call-ID values must not exceed 1024 characters');
  }
  return ids.length > 0 ? ids.join(',') : undefined;
}

export function formatTrafficTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Invalid timestamp'
    : date.toISOString().replace('T', ' ').replace('Z', ' UTC');
}

export { csvCell } from './csv';

export function csvRows(events: readonly SipTrafficEvent[]): string {
  const headers = ['Timestamp', 'Transport', 'Direction', 'Method', 'Response Code', 'SIP Peer ID', 'Call ID', 'Container ID'];
  const rows = events.map((event) => [
    formatTrafficTimestamp(event.timestamp),
    event.transport,
    event.direction,
    event.sipMethod ?? '',
    event.responseCode ?? '',
    event.sipPeerId ?? '',
    event.callId,
    event.containerId,
  ]);
  return csvDocument(headers, rows);
}

export function headerEntries(headers: SipTrafficEvent['headers']): Array<[string, string]> {
  if (!headers) return [];
  return Object.entries(headers).flatMap(([name, value]) =>
    (Array.isArray(value) ? value : [value]).map((entry) => [name, entry] as [string, string]),
  );
}
